import React, { useEffect, useRef, useState } from "react";
import "./barrier.css";
import VanillaTilt from "vanilla-tilt";
import { usePreloadedImages } from "../../../contexts/PreloadedImagesContext";

function Barrier() {
  const { barrierFrames, cardImages } = usePreloadedImages();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameCount = 100;
  const autoFrameLimit = 60;
  const isAutoAnimating = useRef(true);
  const [showTextsSection, setShowTextsSection] = useState(false);

  const cards = [
    {
      id: 1,
      text: "Аналоговый вход",
      image: "AnalogInput.png",
      shadowImage: "AnalogInputShadow.png",
      label: "AI",
      style: {
        width: "100%",
        height: "170px",
        objectFit: "contain" as React.CSSProperties["objectFit"],
      },
    },
    {
      id: 2,
      text: "Аналоговый выход",
      image: "AnalogOutput.png",
      shadowImage: "AnalogOutputShadow.png",
      label: "AO",
      style: {
        width: "100%",
        height: "170px",
        objectFit: "contain" as React.CSSProperties["objectFit"],
      },
    },
    {
      id: 3,
      text: "Дискретный вход",
      image: "DiscreteInput.png",
      shadowImage: "DiscreteInputShadow.png",
      label: "DI",
      style: {
        width: "100%",
        height: "170px",
        objectFit: "contain" as React.CSSProperties["objectFit"],
      },
    },
    {
      id: 4,
      text: "Дискретный выход",
      image: "DiscreteOutput.png",
      shadowImage: "DiscreteOutputShadow.png",
      label: "DO",
      style: {
        width: "100%",
        height: "150px",
        objectFit: "contain" as React.CSSProperties["objectFit"],
      },
    },
    {
      id: 5,
      text: "Температурный вход",
      image: "TemperatureInput.png",
      shadowImage: "TemperatureInputShadow.png",
      label: "TI",
      style: {
        width: "100%",
        height: "180px",
        objectFit: "contain" as React.CSSProperties["objectFit"],
      },
    },
    {
      id: 6,
      text: "Прочие сигналы",
      image: "OtherSignals.png",
      shadowImage: "OtherSignalsShadow.png",
      style: {
        width: "100%",
        height: "120px",
        objectFit: "contain" as React.CSSProperties["objectFit"],
      },
    },
  ];

  const currentFrame = (index: number): string =>
    `https://raw.githubusercontent.com/Nvkva/test4/refs/heads/main/Jpeg_Animation/${index
      .toString()
      .padStart(4, "0")}.png`;

  useEffect(() => {
    if (barrierFrames.length === 0) return;

    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;

    canvas.width = 1158;
    canvas.height = 770;

    let isTextsHaveBeenRendered = false;

    const drawFrame = (index: number) => {
      const frame = barrierFrames[Math.floor(index)];
      if (!frame) return;
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(frame, 0, 0, canvas.width, canvas.height);
    };

    const animate = (startTime: number) => {
      const duration = 3000;
      const now = performance.now();
      const elapsed = now - startTime;

      const progress = Math.min(elapsed / duration, 1);
      const easeOut = Math.pow(progress, 0.5);

      const frame = Math.floor(easeOut * autoFrameLimit) + 1;
      drawFrame(frame);

      if (frame < autoFrameLimit) {
        if (!isTextsHaveBeenRendered) {
          setShowTextsSection(true);
        }
        requestAnimationFrame(() => animate(startTime));
      } else {
        isAutoAnimating.current = false;
      }

      isTextsHaveBeenRendered = true;
    };

    let latestScrollFrame = autoFrameLimit;
    let currentDisplayedFrame = autoFrameLimit;

    const smoothScrollAnimation = () => {
      if (isAutoAnimating.current) return;

      const delta = latestScrollFrame - currentDisplayedFrame;
      if (Math.abs(delta) > 0.2) {
        currentDisplayedFrame += delta * 0.1;
        drawFrame(currentDisplayedFrame);
        requestAnimationFrame(smoothScrollAnimation);
      }
    };

    const handleScroll = () => {
      if (isAutoAnimating.current) return;

      const scrollTop = document.documentElement.scrollTop;
      const maxScrollTop =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrollFraction = Math.pow(scrollTop / maxScrollTop, 1.2);
      const scrollFrames = frameCount - autoFrameLimit;

      latestScrollFrame = autoFrameLimit + scrollFraction * scrollFrames * 2.7;

      requestAnimationFrame(smoothScrollAnimation);
    };

    window.addEventListener("scroll", handleScroll);
    animate(performance.now());

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [barrierFrames]);

  // VanillaTilt init
  useEffect(() => {
    const tiltElements = document.querySelectorAll("[data-tilt]");
    VanillaTilt.init(Array.from(tiltElements) as HTMLElement[], {
      speed: 700,
      glare: true,
      "max-glare": 0.5,
      transition: true,
    });

    return () => {
      Array.from(tiltElements).forEach((el) => {
        (el as any).vanillaTilt?.destroy();
      });
    };
  }, []);

  return (
    <>
      <section className="barrier">
        {/* <div className="overflow-container"> */}
        <div className="barrier-animation">
          <canvas
            className="barrier-canvas"
            ref={canvasRef}
            id="hero-lightpass"
          />
          <div className="barrier-text">
            {showTextsSection && (
              <div className="barrier-description">
                <h3 className="about-barrier-heading">
                  Барьеры искрозащиты серии Т
                </h3>
                <p className="about-barrier">
                  Тонкий корпус <span className="different-color">12,5 мм</span>
                </p>
                <p className="about-barrier">Современная схемотехника</p>
                <p className="about-barrier">
                  Интерфейсные панели для системным разъёмом, в том числе{" "}
                  <span className="different-color">
                    для российских систем управления
                  </span>
                </p>
                <p className="about-barrier">
                  AI, AO, DI - с{" "}
                  <span className="different-color">
                    прозрачной диагностикой поля
                  </span>
                </p>
                <p className="about-barrier">
                  Универсальный температурный преобразователь с градуировками по{" "}
                  <span className="different-color">ГОСТ</span>
                </p>
              </div>
            )}
            {showTextsSection && (
              <div className="barrier__second-description">
                <h3 className="about-barrier-heading">
                  PHD-11TF-29(R) - дискретный вход с прозрачной диагностикой
                </h3>
                <p className="about-barrier">
                  <span className="different-color">Namur</span> выход в систему
                  управления
                </p>
                <p className="about-barrier">
                  <span className="different-color">В 2 раза</span> меньше
                  модулей дискретного входа при{" "}
                  <span className="different-color">
                    поканальной диагностике
                  </span>
                </p>
                <p className="about-barrier">
                  Модель с дублированием сигнала (раздвоитель) PHD-12TF-299(R)
                </p>
                <p className="about-barrier">
                  Двухканальная модель PHD-22TF-2929(R)
                </p>
              </div>
            )}
          </div>
        </div>
        {/* </div> */}
      </section>

      <section className="barrier-options">
        <h2 className="barrier__heading">
          Модели барьеров искрозащиты по типу сигнала
        </h2>
        <ul className="barrier__card-list">
          {cards.map((card) => (
            <li
              key={card.id}
              className={`barrier__card-list-item${
                card.id === 6 ? " barrier__card-list-item--full" : ""
              }`}
              data-tilt
              data-tilt-glare
              data-tilt-speed="700"
            >
              <div
                className="barrier__card-image-wrapper"
                style={{
                  gridArea: "image",
                  ...(card.id === 4 ? { marginTop: "20px" } : {}),
                  ...(card.id === 6 ? { marginTop: "30px" } : {}),
                }}
              >
                {card.shadowImage && (
                  <img
                    src={cardImages[card.shadowImage]?.src || ""}
                    alt={`${card.text} тень`}
                    className="barrier__card-image-shadow"
                    style={card.style}
                  />
                )}
                <img
                  src={cardImages[card.image]?.src || ""}
                  alt={card.text}
                  className="barrier__card-image"
                  style={card.style}
                />
              </div>
              {card.label?.length && (
                <div
                  className="barrier__card-label"
                  style={{ gridArea: "label" }}
                >
                  <span>{card.label}</span>
                </div>
              )}
              <div
                className="barrier__card-list-footer"
                style={{ gridArea: "footer" }}
              >
                <p className="barrier__card-list-text">{card.text}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}

export default Barrier;
